import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../../libraries/state_reducers/store";
import UtilityHelper from "../../../../service/utilities/UtilityHelper";
import { textCase } from "../../../../service/ui/UI";
import SignupPersonalInformationProvider from "./SignupPersonalInformationProvider";
import ShowToast from "../../../../widget/common/ui/Toast";
import AppTranslation from "../../../../service/app_translation/AppTranslation";
import { SignupResult } from "../../../../model/onboarding/signup/SignupModel";
import SessionStorage from "../../../../service/storage/SessionStorage";
import StorageKeys from "../../../../const/StorageKeys";
import Loader from "../../../../widget/common/ui/Loader";
import { phoneMobileDefault } from "../../../../const/MobileConstant";
import { locationDefault } from "../../../../const/LocationConstant";
import { useDispatch } from "react-redux";
import { getSubmitGEO } from "../../../../libraries/state_reducers/country_state_city_area/CountyStateCityAreaReducer";
import { LocationGeoResult } from "../../../../model/country_state_city_area/GeoLocationModel";
import { CountryCodeList } from "../../../../model/country_code/CountryCodeModel";
import { SocialSignInResponseType } from "../../../../libraries/types_interface/social/LoginSocialConnectTypes";

type SignupPersonalInformationTypes = {
  agreeStatus: boolean;
  toggleAgreeStatus: () => void;

  firstNameField: string;
  lastNameField: string;
  emailField: string;
  passwordField: string;
  passwordConfirmField: string;
  phoneField: string;
  phoneCodeField: string;
  phoneCodeIdField: string;

  countryId: string;
  countryName: string;
  stateId: string;
  stateName: string;
  cityId: string;
  cityName: string;
  areaId: string;
  areaName: string;

  submitDetail: () => void;

  profileErrors: ProfileErrorType;
  profileValidate: boolean;

  verificationType: "email" | "phone";

  handleOnChange: (
    name: string,
    value: string,
    isValid: boolean | undefined
  ) => void;

  applyCountryCode: (countryCodeDetail: CountryCodeList) => void;
};

type ProfileErrorType = {
  firstNameField: boolean;
  lastNameField: boolean;
  emailField: boolean;
  phoneField: boolean;
  passwordField: boolean;
  passwordConfirmField: boolean;
  cityName: boolean;
};

export type SignupSocialParamType = {
  socialMediaStatus: string;
  socialMediaType: string;
  socialMediaAppID: string;
  socialMediaAccessToken: string;
  socialMediaProfileImage: string;
};

const SignupPersonalInformationContext = createContext<
  SignupPersonalInformationTypes | undefined
>(undefined);

export const SignupPersonalInformationContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const dispatch = useDispatch();

  const geoDispatch = useSelector(
    (state: RootState) => state.countryStateCityArea.geo
  );

  const geoRefreshDispatch = useSelector(
    (state: RootState) => state.countryStateCityArea.timestamp
  );

  const verificationTypeSession: string =
    SessionStorage.getValue(StorageKeys.verification_type) || "email";

  const verificationType: "email" | "phone" = !["email", "phone"].includes(
    verificationTypeSession
  )
    ? "email"
    : (verificationTypeSession as "email" | "phone");

  const profileErrorsRef = useRef<ProfileErrorType>({
    firstNameField: false,
    lastNameField: false,
    emailField: false,
    phoneField: verificationType === "phone" ? false : true,
    passwordField: false,
    passwordConfirmField: false,
    cityName: true,
  });

  const [profileErrors, setProfileErrors] = useState<ProfileErrorType>({
    firstNameField: profileErrorsRef.current.firstNameField,
    lastNameField: profileErrorsRef.current.lastNameField,
    emailField: profileErrorsRef.current.emailField,
    phoneField: profileErrorsRef.current.phoneField,
    passwordField: profileErrorsRef.current.passwordField,
    passwordConfirmField: profileErrorsRef.current.passwordConfirmField,
    cityName: profileErrorsRef.current.cityName,
  });

  const [profileValidate, setProfileValidate] = useState<boolean>(false);

  const [firstNameField, setFirstNameField] = useState<string>("");
  const [lastNameField, setLastNameField] = useState<string>("");
  const [emailField, setEmailField] = useState<string>("");
  const [passwordField, setPasswordField] = useState<string>("");
  const [passwordConfirmField, setPasswordConfirmField] = useState<string>("");
  const [phoneField, setPhoneField] = useState<string>("");
  const [phoneCodeField, setPhoneCodeField] = useState<string>(
    phoneMobileDefault.phone_code
  );
  const [phoneCodeIdField, setPhoneCodeIdField] = useState<string>(
    phoneMobileDefault.phone_code_id
  );

  const [countryId, setCountryIdField] = useState<string>(
    locationDefault.country_id
  );
  const [countryName, setCountryNameField] = useState<string>(
    locationDefault.country_name
  );
  const [stateId, setStateIdField] = useState<string>(locationDefault.state_id);
  const [stateName, setStateNameField] = useState<string>(
    locationDefault.state_name
  );
  const [cityId, setCityIdField] = useState<string>(locationDefault.city_id);
  const [cityName, setCityNameField] = useState<string>(
    locationDefault.city_name
  );

  const areaId: string = locationDefault.area_id;
  const areaName: string = locationDefault.area_name;

  /* const [areaId, setAreaIdField] = useState<string>(locationDefault.area_id);
  const [areaName, setAreaNameField] = useState<string>(
    locationDefault.area_name
  ); */

  const [agreeStatus, setAgreeStatus] = useState<boolean>(false);
  const [socialParam, setSocialParam] = useState<SignupSocialParamType>({
    socialMediaStatus: "0",
    socialMediaType: "0",
    socialMediaAppID: "",
    socialMediaAccessToken: "",
    socialMediaProfileImage: "",
  });

  const toggleAgreeStatus = () => {
    setAgreeStatus(!agreeStatus);
  };

  const handleOnChange = (
    fieldName: string,
    fieldValue: string,
    isValid: boolean | undefined
  ) => {
    fieldName = fieldName.toLowerCase();

    let profileErrorsTemp: ProfileErrorType = {
      ...profileErrors,
    };

    if (fieldName === `first_name`) {
      setFirstNameField(fieldValue);
      profileErrorsTemp = {
        ...profileErrorsTemp,
        firstNameField: isValid ?? true,
      };
    } else if (fieldName === `last_name`) {
      setLastNameField(fieldValue);
      profileErrorsTemp = {
        ...profileErrorsTemp,
        lastNameField: isValid ?? true,
      };
    } else if (fieldName === `email`) {
      setEmailField(fieldValue);
      profileErrorsTemp = {
        ...profileErrorsTemp,
        emailField: isValid ?? true,
      };
    } else if (fieldName === `phone_number_optional`) {
      setPhoneField(fieldValue);
      profileErrorsTemp = {
        ...profileErrorsTemp,
        phoneField: isValid ?? true,
      };
    } else if (fieldName === `password`) {
      setPasswordField(fieldValue);
      profileErrorsTemp = {
        ...profileErrorsTemp,
        passwordField: isValid ?? true,
      };
    } else if (fieldName === `password_confirm`) {
      setPasswordConfirmField(fieldValue);
      profileErrorsTemp = {
        ...profileErrorsTemp,
        passwordConfirmField: isValid ?? true,
      };
    } else if (fieldName === `city_of_residence`) {
      setCityNameField(fieldValue);
      /* profileErrorsTemp = {
        ...profileErrorsTemp,
        cityName: isValid ?? true,
      }; */
    }

    setProfileValidate(false);
    setProfileErrors(profileErrorsTemp);
    profileErrorsRef.current = profileErrorsTemp;
  };

  const validateErrors = (): boolean => {
    let errorCounter = 0;

    if (
      [
        profileErrorsRef.current.firstNameField,
        profileErrorsRef.current.lastNameField,
        //profileErrorsRef.current.emailField,
        //profileErrorsRef.current.phoneField,
        profileErrorsRef.current.passwordField,
        profileErrorsRef.current.passwordConfirmField,
      ].includes(false)
    ) {
      errorCounter++;
    }

    if (
      profileErrorsRef.current.passwordField &&
      profileErrorsRef.current.passwordConfirmField
    ) {
      if (
        profileErrorsRef.current.passwordField !==
        profileErrorsRef.current.passwordConfirmField
      ) {
        ShowToast.info(AppTranslation.text("password_mismatch"));
        errorCounter++;
      }
    }

    if (verificationType === "email") {
      if (
        !UtilityHelper.isEmail(emailField) ||
        UtilityHelper.isEmpty(emailField)
      ) {
        errorCounter++;
      }
    } else if (verificationType === "phone") {
      if (
        UtilityHelper.isEmpty(phoneCodeIdField) ||
        UtilityHelper.isEmpty(phoneField) ||
        !UtilityHelper.phoneNumberValid(phoneField)
      ) {
        errorCounter++;
      }
    }

    /* if (
      UtilityHelper.isNotEmpty(phoneCodeIdField) &&
      UtilityHelper.isEmpty(phoneField)
    ) {
      errorCounter++;
      ShowToast.info(AppTranslation.text("phone_number_is_empty"));
    } */

    if (!agreeStatus && errorCounter === 0) {
      ShowToast.info(AppTranslation.text("agree_terms"));
      errorCounter++;
    }

    //console.log(profileErrorsRef.current);
    console.log(errorCounter);

    setProfileValidate(true);
    return errorCounter === 0 ? true : false;
  };

  const submitDetail = async () => {
    if (validateErrors()) {
      Loader.modal();
      const signupResult: SignupResult | null =
        await SignupPersonalInformationProvider.submitPersonalInfoAPI({
          first_name: firstNameField,
          last_name: lastNameField,
          email: emailField,
          password: passwordField,
          mobile_code_id: phoneCodeIdField,
          mobile_no: phoneField,
          city_id: cityId,
          social: socialParam,
        });
      Loader.modal(false);

      if (signupResult != null) {
        SessionStorage.saveValue(
          StorageKeys.signup_verification_detail,
          JSON.stringify(signupResult)
        );

        SignupPersonalInformationProvider.navigateNext();
      }
    }
  };

  const applyCity = () => {
    if (geoDispatch != null) {
      /* let params = {
        cId: "0",
        sId: "0",
        citId: "0",
      }; */

      if (geoDispatch?.country !== null) {
        const cId: string = geoDispatch?.country?.country_id! ?? "0";
        //params = { ...params, cId: cId };

        setCountryIdField(cId);
        setCountryNameField(
          textCase(geoDispatch?.country?.country_name! ?? "")
        );
      }

      if (geoDispatch?.state !== null) {
        const sId: string = geoDispatch?.state?.state_id! ?? "0";
        //params = { ...params, sId: sId };

        setStateIdField(sId);
        setStateNameField(textCase(geoDispatch?.state?.state_name! ?? ""));
      }

      if (geoDispatch?.city != null) {
        setCityIdField(geoDispatch?.city?.city_id! ?? "0");
        setCityNameField(textCase(geoDispatch?.city?.city_name!));
      }
    }
  };

  const applyCountryCode = (countryCodeDetail: CountryCodeList) => {
    if (countryCodeDetail != null) {
      setPhoneCodeField(countryCodeDetail?.country_phonecode!);
      setPhoneCodeIdField(countryCodeDetail?.country_id!);
    }
  };

  const resetForm = () => {
    const thirdPatrySession: string =
      SessionStorage.getValue(StorageKeys.signup_third_party_connect_detail) ??
      "";

    let fName: string = "",
      lName: string = "",
      email: string = "";

    if (UtilityHelper.isNotEmpty(thirdPatrySession)) {
      const thirdPatry: SocialSignInResponseType =
        JSON.parse(thirdPatrySession);

      console.log(thirdPatry);
      fName = thirdPatry.first_name ?? "";
      lName = thirdPatry.last_name ?? "";
      email = thirdPatry.email!;

      setSocialParam({
        socialMediaStatus: "1",
        socialMediaType: thirdPatry.socialType!,
        socialMediaAppID: thirdPatry.id,
        socialMediaAccessToken:
          thirdPatry.socialType! === "1" ? thirdPatry.token! : thirdPatry.email,
        socialMediaProfileImage: thirdPatry.photo,
      });
    }

    setFirstNameField(fName);
    setLastNameField(lName);
    setEmailField(email);
    setPasswordField("");
    setPasswordConfirmField("");
    setPhoneField("");
    setPhoneCodeField(phoneMobileDefault.phone_code);
    setCountryNameField(locationDefault.country_name);
    setStateNameField(locationDefault.state_name);
    setCityNameField(locationDefault.city_name);
  };

  useEffect(() => {
    applyCity();
    // eslint-disable-next-line
  }, [geoRefreshDispatch]);

  useEffect(() => {
    resetForm();

    const locationDetail: LocationGeoResult = {
      country_id: locationDefault.country_id,
      country_name: locationDefault.country_name,
      state_id: locationDefault.state_id,
      state_name: locationDefault.city_id,
      city_id: locationDefault.state_id,
      city_name: locationDefault.city_name,
      area_id: locationDefault.area_id,
      area_name: locationDefault.area_name,
    };

    dispatch(
      getSubmitGEO({
        country: locationDetail,
        state: locationDetail,
        city: locationDetail,
        area: locationDetail,
      })
    );
    // eslint-disable-next-line
  }, []);

  return (
    <SignupPersonalInformationContext.Provider
      value={{
        agreeStatus,
        toggleAgreeStatus,

        firstNameField,
        lastNameField,
        emailField,
        passwordField,
        passwordConfirmField,
        phoneField,
        phoneCodeField,
        phoneCodeIdField,

        countryId,
        countryName,
        stateId,
        stateName,
        cityId,
        cityName,
        areaId,
        areaName,

        submitDetail,

        profileErrors,
        profileValidate,

        verificationType,

        handleOnChange,

        applyCountryCode,
      }}
    >
      {children}
    </SignupPersonalInformationContext.Provider>
  );
};

// IMPORT / USE CONTEXT ///////////////////////////////////////////////////////////////////
export const SignupPersonalInformationContextImport = () => {
  const context = useContext(SignupPersonalInformationContext);
  if (!context) {
    throw new Error(
      "SignupPersonalInformationContextImport must be used within a SignupPersonalInformationContextProvider"
    );
  }
  return context;
};
